import { JSONSchema7 } from 'json-schema';

import { isOpenAIFunctions, isOpenAITools } from '@/utils/ai-tools';
import { getAPIUrlVercel } from '@/utils/get-api-url';

import {
  DatasetSchema,
  DatasetTransformationSchema,
  DatasetTransformationType,
  ExampleSchema,
  ExampleValidateSchema,
  RunSchema,
  StoredMessage,
} from '../../types/schema';
import {
  getFlattenedArrayOfGenerations,
  getInvocationParams,
  getMessageAsStoredMessage,
  maybeGetArrayOfMessages,
} from '../../utils/messages';
import { JsonSchema7 } from '../SchemaEditor/JsonSchema';

// IF YOU CHANGE THIS LOGIC YOU MUST CHANGE THE PYTHON CODE IN examples/utils.py

export function getDataType(
  dataset: DatasetSchema | undefined,
  run: Pick<RunSchema, 'run_type' | 'inputs'> | undefined
): DatasetSchema['data_type'] {
  return (
    dataset?.data_type ??
    (run?.run_type === 'llm' && run?.inputs?.messages
      ? 'chat'
      : run?.run_type === 'llm'
      ? 'llm'
      : 'kv')
  );
}

function getEmptyInputs(dataType: DatasetSchema['data_type']) {
  if (dataType === 'chat') {
    return JSON.stringify(
      {
        input: [
          {
            type: 'human',
            data: {
              content: '',
            },
          },
        ],
      },
      null,
      2
    );
  } else if (dataType === 'llm') {
    return JSON.stringify({ input: '' }, null, 2);
  } else {
    return JSON.stringify({}, null, 2);
  }
}

function getEmptyOutputs(dataType: DatasetSchema['data_type']) {
  if (dataType === 'chat') {
    return JSON.stringify(
      {
        output: {
          type: 'ai',
          data: {
            content: '',
          },
        },
      },
      null,
      2
    );
  } else if (dataType === 'llm') {
    return JSON.stringify({ output: '' }, null, 2);
  } else {
    return JSON.stringify({}, null, 2);
  }
}

type ChatInputs = {
  input: (StoredMessage | undefined)[] | undefined;
  functions?: object[];
};

export function getDefaultInputsObject(
  run: Pick<RunSchema, 'inputs' | 'outputs' | 'extra' | 'id'> | undefined,
  example: ExampleSchema | undefined,
  schema: JSONSchema7 | undefined
): RunSchema['inputs'] {
  return run?.inputs ?? example?.inputs ?? getJsonFromSchema(schema);
}

export function getDefaultOutputsObject(
  run: Pick<RunSchema, 'inputs' | 'outputs' | 'extra' | 'id'> | undefined,
  example: ExampleSchema | undefined,
  schema: JSONSchema7 | undefined
): RunSchema['outputs'] {
  return run?.outputs ?? example?.outputs ?? getJsonFromSchema(schema);
}

export function getDefaultInputs(
  dataType: DatasetSchema['data_type'],
  run: Pick<RunSchema, 'inputs' | 'outputs' | 'extra' | 'id'> | undefined,
  example: ExampleSchema | undefined,
  schema: JSONSchema7 | undefined
): string {
  if (run) {
    if (dataType === 'chat') {
      const params = getInvocationParams(run);
      const rawTools = params?.tools;
      const tools = isOpenAITools(rawTools) ? rawTools : undefined;

      const rawFunctions = params?.functions;
      const functions = isOpenAIFunctions(rawFunctions)
        ? rawFunctions
        : undefined;

      const inputs: ChatInputs = {
        input: maybeGetArrayOfMessages(run.inputs?.messages)?.map?.(
          getMessageAsStoredMessage
        ),
        functions: tools?.map((i) => i.function) ?? functions,
      };

      return JSON.stringify(inputs, null, 2);
    } else if (dataType === 'llm') {
      return JSON.stringify(
        { input: run.inputs?.prompt ?? run.inputs?.prompts?.[0] },
        null,
        2
      );
    }
  }
  const source = example ?? run;
  return source
    ? JSON.stringify(source.inputs, null, 2)
    : schema
    ? JSON.stringify(getJsonFromSchema(schema), null, 2)
    : getEmptyInputs(dataType);
}

export function getDefaultOutputs(
  dataType: DatasetSchema['data_type'],
  run: Pick<RunSchema, 'inputs' | 'outputs' | 'extra' | 'id'> | undefined,
  example: ExampleSchema | undefined,
  schema: JSONSchema7 | undefined
): string {
  if (run) {
    const generations = getFlattenedArrayOfGenerations(run.outputs);
    if (dataType === 'chat') {
      return JSON.stringify(
        {
          output: getMessageAsStoredMessage(generations[0]?.message),
        },
        null,
        2
      );
    } else if (dataType === 'llm') {
      return JSON.stringify({ output: generations[0]?.text }, null, 2);
    }
  }
  const source = example ?? run;
  return source
    ? JSON.stringify(source.outputs, null, 2)
    : schema
    ? JSON.stringify(getJsonFromSchema(schema), null, 2)
    : getEmptyOutputs(dataType);
}

type JSONSchemaType =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'object'
  | 'array'
  | 'null';

function hasType(schema: JSONSchema7, type: JSONSchemaType): boolean {
  if (!schema.type) return false;
  return (
    schema.type === type ||
    (Array.isArray(schema.type) && schema.type.includes(type))
  );
}

function defaultValue(schema: JSONSchema7) {
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.enum) && schema.enum.length > 0)
    return schema.enum[0];
  return undefined;
}

function getDefaultMessage() {
  return {
    role: 'system',
    content: '',
  };
}

export function getDefaultTool() {
  return {
    type: 'function',
    function: {
      name: 'Tool',
      description: 'Tool description',
      parameters: {
        type: 'object',
        properties: {
          param: {
            type: 'string',
            description: 'Param description',
          },
        },
      },
    },
  };
}

export function getJsonFromSchema(schema?: JSONSchema7): any {
  if (typeof schema !== 'object' || !schema) {
    return {};
  }

  if (schema.$ref?.includes('message.json')) {
    return getDefaultMessage();
  }

  if (schema.$ref?.includes('tooldef.json')) {
    return getDefaultTool();
  }

  // Handle anyOf/oneOf with $refs
  if (schema.anyOf || schema.oneOf) {
    const options = schema.anyOf || schema.oneOf;
    const option = options?.[0];

    return getJsonFromSchema(option as JSONSchema7);
  }

  if (hasType(schema, 'object')) {
    const result: Record<string, any> = {};
    if (schema.properties) {
      for (const [key, propSchema] of Object.entries(schema.properties)) {
        result[key] = getJsonFromSchema(propSchema as JSONSchema7);
      }
    }
    return result;
  }

  if (hasType(schema, 'array')) {
    if (
      schema.items &&
      !Array.isArray(schema.items) &&
      typeof schema.items === 'object'
    ) {
      const itemSchema = schema.items as JSONSchema7;
      if (itemSchema.$ref) {
        let object = {};
        if (itemSchema.$ref.includes('message.json')) {
          object = getDefaultMessage();
        }

        if (itemSchema.$ref.includes('tooldef.json')) {
          object = getDefaultTool();
        }
        return Array(schema.minItems ?? 1).fill(object);
      }
    }
    return schema.minItems ? Array(schema.minItems).fill(null) : [];
  }

  if (hasType(schema, 'string')) {
    return defaultValue(schema) ?? '';
  }

  if (hasType(schema, 'number') || hasType(schema, 'integer')) {
    return defaultValue(schema) ?? 0;
  }

  if (hasType(schema, 'boolean')) {
    return defaultValue(schema) ?? false;
  }

  if (hasType(schema, 'null')) {
    return null;
  }

  return {};
}

export const supportedTypes = [
  'string',
  'number',
  'boolean',
  'null',
  'integer',
  'object',
  'array',
  'message',
  'tool',
];

export function isSimpleParamsEditorAvailable(value: JsonSchema7) {
  if (value.type == null) {
    if (
      value.$ref?.includes('message.json') ||
      value.$ref?.includes('tooldef.json')
    ) {
      return true;
    }
    return false;
  }
  if (Array.isArray(value.type)) {
    return value.type.every((type) => supportedTypes.includes(type));
  }

  return supportedTypes.includes(value.type);
}

export function isJsonSchemaType(
  haystack: JsonSchema7['type'],
  needle: string
) {
  if (haystack == null) return false;
  if (Array.isArray(haystack)) {
    return haystack.includes(needle);
  }

  return haystack === needle;
}
export function changeSchemaType(
  schema: JsonSchema7,
  type: string | string[] | undefined
): JsonSchema7 {
  const baseSchemaUrl = getAPIUrlVercel();
  const newTypes = Array.isArray(type) ? type : type ? [type] : [];

  const normalTypes = newTypes.filter(
    (t) => !['message', 'array', 'tool'].includes(t)
  );
  const refTypes = newTypes.filter((t) => ['message', 'tool'].includes(t));
  const hasArrayType = newTypes.includes('array');

  const refSchemas = refTypes.map((t) => ({
    $ref: `${baseSchemaUrl}/public/schemas/v1/${
      t === 'tool' ? 'tooldef' : 'message'
    }.json`,
  }));

  const newSchema: JsonSchema7 = { ...schema };

  // Case 1: Only normal types and array types
  if ((normalTypes.length > 0 || hasArrayType) && refTypes.length === 0) {
    newSchema.type = normalTypes.length === 1 ? normalTypes[0] : normalTypes;
    if (hasArrayType) {
      newSchema.items = { type: 'string' };
      newSchema.type = Array.isArray(newSchema.type)
        ? [...newSchema.type, 'array']
        : [newSchema.type as string, 'array'];
    } else {
      delete newSchema.items;
    }
    delete newSchema.anyOf;
    delete newSchema.$ref;
  }
  // Case 2: Mix of normal types and ref types
  else if ((normalTypes.length > 0 || hasArrayType) && refTypes.length > 0) {
    newSchema.anyOf = [...normalTypes.map((t) => ({ type: t })), ...refSchemas];
    if (hasArrayType) {
      newSchema.anyOf.push({ type: 'array', items: { type: 'string' } });
    }
    delete newSchema.items;
    delete newSchema.type;
    delete newSchema.$ref;
  }
  // Case 3: Only ref types
  else if (refTypes.length > 0) {
    if (refTypes.length === 1) {
      newSchema.$ref = refSchemas[0].$ref;
      delete newSchema.anyOf;
    } else {
      newSchema.anyOf = refSchemas;
      delete newSchema.$ref;
    }
    delete newSchema.type;
    delete newSchema.items;
  }

  // Case 4: No types specified
  else {
    delete newSchema.type;
    delete newSchema.anyOf;
    delete newSchema.$ref;
    delete newSchema.items;
  }

  if (!newTypes.includes('object')) {
    delete newSchema.properties;
  }

  if (newSchema.type?.length === 1) {
    newSchema.type = newSchema.type[0];
  }

  return newSchema;
}

export function checkIsValidOrThrowError(json: string) {
  if (json && (typeof json !== 'object' || Array.isArray(json))) {
    throw new Error('Invalid JSON');
  }
}

export function getExampleValidateFromRun(
  run: RunSchema,
  datasetId: string
): ExampleValidateSchema {
  return {
    inputs: run.inputs,
    outputs: run.outputs,
    dataset_id: datasetId,
    source_run_id: run.id,
    metadata: {},
  };
}

export const getTypeFromRef = (ref: string | undefined) => {
  if (ref) {
    if (ref.includes('message.json')) {
      return 'message';
    } else if (ref.includes('tooldef.json')) {
      return 'tool';
    }
  }
  return undefined;
};

export const getTypeValue = (schema: JsonSchema7): string[] => {
  if (schema.anyOf) {
    return schema.anyOf
      .map((subSchema) => {
        if (subSchema.$ref) {
          return getTypeFromRef(subSchema.$ref);
        }
        return Array.isArray(subSchema.type)
          ? subSchema.type[0]
          : subSchema.type;
      })
      .filter((t): t is string => t != null);
  }
  if (schema.$ref) {
    const type = getTypeFromRef(schema.$ref);
    return type != null ? [type] : [];
  }
  return Array.isArray(schema.type)
    ? schema.type
    : schema.type
    ? [schema.type]
    : [];
};

export function getAvailableTransformations(
  value: JsonSchema7,
  path: string[],
  isArrayChild?: boolean
) {
  const valueType = getTypeValue(value);
  const inputOutput = path[0];

  return Object.values(DatasetTransformationType).filter((t) => {
    switch (t) {
      case DatasetTransformationType.CONVERT_TO_OPENAI_TOOL:
        return (
          valueType.includes('tool') &&
          inputOutput === 'inputs' &&
          path.length === 2
        );
      case DatasetTransformationType.CONVERT_TO_OPENAI_MESSAGE:
        return valueType.includes('message');
      case DatasetTransformationType.REMOVE_SYSTEM_MESSAGES:
        return (
          valueType.includes('message') &&
          inputOutput === 'inputs' &&
          isArrayChild
        );
      case DatasetTransformationType.REMOVE_EXTRA_FIELDS:
        return valueType.includes('object');
      case DatasetTransformationType.EXTRACT_TOOLS_FROM_RUN:
        // deprecated
        return false;
      default:
        return true;
    }
  });
}

export function mapTransformationPathMatches(
  oldKey: string,
  path: string[],
  transformations: DatasetTransformationSchema[],
  fn: (t: DatasetTransformationSchema) => DatasetTransformationSchema | null
): DatasetTransformationSchema[] {
  const oldPath = [...path, oldKey];
  const oldPathStr = oldPath.join('.');

  return transformations
    .map((t) => {
      if (
        t.path.join('.') === oldPathStr ||
        t.path.join('.').startsWith(oldPathStr + '.')
      ) {
        return fn(t);
      }
      return t;
    })
    .filter((t) => t !== null) as DatasetTransformationSchema[];
}
